import { api_fetch } from "./client"

/** A deduction an attachment already produced. */
export interface LinkedDeduction {
  id: string
  invoice_number: string
  invoice_amount: number
  /** `deduction`, `invoice`, `repayment`… — a remittance books far more payments than charges. */
  kind: string
}

/** A remittance-looking attachment sitting in synced email. */
export interface EmailRemittanceFile {
  attachment_id: string
  name: string
  s3_uri: string
  content_type: string
  kind?: string | null
  message_id: string
  subject: string
  from_email?: string | null
  from_name?: string | null
  date?: string | null
  /** Why we think this is a remittance, e.g. `subject: payment advice`. */
  signals: string[]
  /**
   * At least one signal was unambiguously about a payment. Weak-only matches (a statement of
   * account, a bare ACH note) are as often a supplier's AP paperwork, so they rank below.
   */
  strong: boolean
  /** Already OCR'd, classified, or referenced by a deduction. Sorts to the bottom, never hidden. */
  already_parsed: boolean
  parsed_reason?: string | null
  linked_deduction_count: number
  /** The transactions this file already produced, so a parsed row can link straight to them. */
  linked_deductions: LinkedDeduction[]
  /**
   * An identical document is already listed above — the same attachment re-sent on a `FW:` or
   * `Re:` of its own thread, stored under a fresh URI each time.
   */
  is_duplicate: boolean
  /** The row this one duplicates, so the UI can point at the copy that counts. */
  duplicate_of?: string | null
  /** Configured distributor sources this email matches, most specific first (label chips + filter). */
  email_source_ids: string[]
}

export interface EmailRemittanceQuery {
  q?: string
  limit?: number
  days_back?: number
}

export async function listEmailRemittances(query: EmailRemittanceQuery = {}): Promise<EmailRemittanceFile[]> {
  const params = new URLSearchParams()
  if (query.q) params.set("q", query.q)
  if (query.limit) params.set("limit", String(query.limit))
  if (query.days_back) params.set("days_back", String(query.days_back))
  const suffix = params.toString() ? `?${params.toString()}` : ""

  const res = await api_fetch<EmailRemittanceFile[]>(`/file-imports/email-remittances${suffix}`)
  if (!res.ok) {
    throw new Error("Failed to load email remittances")
  }
  return res.value.data
}

export interface AttachToDeductionResponse {
  deduction_id: string
  invoice_number: string
  /** True when this file is now the deduction's canonical backup. */
  set_as_backup: boolean
  /** Present when the file was attached but an existing backup was kept instead. */
  note?: string | null
}

/**
 * Attach an email file to a deduction that already exists, instead of parsing new ones out of it.
 *
 * The file always lands in the deduction's Files. It additionally becomes the canonical backup only
 * when the deduction hasn't got one — an existing backup is never replaced by a click.
 */
export async function attachEmailFileToDeduction(
  attachment_id: string,
  deduction_id: string,
): Promise<AttachToDeductionResponse> {
  const res = await api_fetch<AttachToDeductionResponse>("/file-imports/email-remittances/attach", {
    method: "POST",
    body: { attachment_id, deduction_id },
  })
  if (!res.ok) {
    throw new Error(res.error.message || "Could not attach the file")
  }
  return res.value.data
}

/** One connected mailbox and when its sync last finished. */
export interface MailboxSync {
  integration_id: string
  provider: string
  username: string
  last_completed_at?: string | null
  running: boolean
}

export interface EmailSyncStatus {
  mailboxes: MailboxSync[]
  /** Oldest completion among mailboxes that have run — everything that syncs is at least this fresh. */
  last_completed_at?: string | null
  /** Connected mailboxes with no successful sync on record (often a stale duplicate integration). */
  never_synced: number
  running: boolean
}

export async function getEmailSyncStatus(): Promise<EmailSyncStatus> {
  const res = await api_fetch<EmailSyncStatus>("/file-imports/email-sync")
  if (!res.ok) {
    throw new Error("Could not read the email sync status")
  }
  return res.value.data
}

export async function startEmailSync(): Promise<EmailSyncStatus> {
  const res = await api_fetch<EmailSyncStatus>("/file-imports/email-sync", { method: "POST", body: {} })
  if (!res.ok) {
    throw new Error(res.error.message || "Could not start an email sync")
  }
  return res.value.data
}
